import { useCallback, useRef, useState } from "react";
import {
  X,
  File,
  Image,
  VideoCamera,
  MusicNote,
  WarningCircle,
} from "@/components/ui/icons";
import { LoadingIcon } from "@/components/ui/loading-icon";
import { Button } from "@/components/ui/button";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  type PendingAttachment,
  type UploadedAttachment,
  uploadFile,
  createPreviewUrl,
  revokePreviewUrl,
} from "@/hooks/use-attachment-upload";
import { validatePastedFiles, showPasteErrors } from "@/lib/clipboard-utils";

interface AttachmentUploaderProps {
  pendingAttachments: PendingAttachment[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (id: string) => void;
  maxFiles?: number;
}

const MAX_FILES = 10;

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface FileTypeIconProps {
  type: string;
  size: number;
  className?: string;
}

function FileTypeIcon({ type, size, className }: FileTypeIconProps) {
  if (type.startsWith("image/"))
    return <Image size={size} className={className} />;
  if (type.startsWith("video/"))
    return <VideoCamera size={size} className={className} />;
  if (type.startsWith("audio/"))
    return <MusicNote size={size} className={className} />;
  return <File size={size} className={className} />;
}

interface PendingFilePreviewProps {
  attachment: PendingAttachment;
  onRemove: () => void;
}

function PendingFilePreview({ attachment, onRemove }: PendingFilePreviewProps) {
  const isImage = attachment.file.type.startsWith("image/");

  return (
    <div
      className={cn(
        "relative group rounded-lg transition-colors",
        "bg-secondary/50 border border-border",
        "hover:bg-secondary hover:border-muted-foreground/30",
        attachment.status === "error" && "border-destructive",
      )}
    >
      {/* Preview content */}
      {isImage && attachment.previewUrl ? (
        <div className="w-20 h-20 rounded-lg overflow-hidden">
          <img
            src={attachment.previewUrl}
            alt={attachment.file.name}
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="w-20 h-20 flex flex-col items-center justify-center p-2 rounded-lg overflow-hidden">
          <FileTypeIcon
            type={attachment.file.type}
            size={24}
            className="text-muted-foreground mb-1"
          />
          <span className="text-[10px] text-muted-foreground truncate w-full text-center">
            {attachment.file.name.length > 10
              ? attachment.file.name.slice(0, 8) + "..."
              : attachment.file.name}
          </span>
        </div>
      )}

      {/* Overlay for status */}
      {attachment.status === "uploading" && (
        <div className="absolute inset-0 bg-overlay/50 flex items-center justify-center">
          <LoadingIcon size={20} className="text-foreground" />
        </div>
      )}

      {attachment.status === "error" && (
        <div
          className="absolute inset-0 bg-destructive/50 flex flex-col items-center justify-center gap-0.5 cursor-default"
          title={attachment.error ?? "Upload failed"}
        >
          <WarningCircle size={20} className="text-foreground" />
          <span className="text-[9px] text-foreground/90 px-1 text-center line-clamp-2">
            {attachment.error ?? "Failed"}
          </span>
        </div>
      )}

      {/* Remove button */}
      <ToolbarIconButton
        type="button"
        onClick={onRemove}
        className={cn(
          "absolute -right-1.5 -top-1.5 z-10 h-5 w-5 bg-muted text-foreground",
          "opacity-0 transition-all group-hover:opacity-100",
          "hover:bg-destructive hover:text-destructive-foreground",
        )}
        aria-label="Remove attachment"
      >
        <X size={12} weight="Bold" />
      </ToolbarIconButton>

      {/* Size label */}
      <div className="absolute bottom-0 left-0 right-0 px-1 py-0.5 bg-overlay/50 text-[9px] text-foreground text-center">
        {formatFileSize(attachment.file.size)}
      </div>
    </div>
  );
}

export function AttachmentUploader({
  pendingAttachments,
  onAddFiles,
  onRemoveFile,
  maxFiles = MAX_FILES,
}: AttachmentUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;

      const { valid, errors } = validatePastedFiles(
        Array.from(files),
        pendingAttachments.length,
      );

      if (valid.length > 0) {
        onAddFiles(valid);
      }
      showPasteErrors(errors);
    },
    [pendingAttachments.length, onAddFiles],
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  if (pendingAttachments.length === 0) {
    return (
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />
    );
  }

  return (
    <div className="px-4 py-2 border-t border-border">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={(e) => handleFileSelect(e.target.files)}
        className="hidden"
      />

      <div className="flex flex-wrap gap-2">
        {pendingAttachments.map((attachment) => (
          <PendingFilePreview
            key={attachment.id}
            attachment={attachment}
            onRemove={() => onRemoveFile(attachment.id)}
          />
        ))}

        {pendingAttachments.length < maxFiles && (
          <Button
            type="button"
            variant="outline"
            onClick={openFilePicker}
            className={cn(
              "h-20 w-20 rounded-lg border-2 border-dashed border-border bg-transparent",
              "text-muted-foreground shadow-none hover:border-foreground/50 hover:bg-transparent hover:text-foreground",
            )}
            aria-label="Add more files"
          >
            <span className="text-2xl">+</span>
          </Button>
        )}
      </div>
    </div>
  );
}

// Export hooks for managing pending attachments
export function usePendingAttachments() {
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);

  const addFiles = useCallback((files: File[]) => {
    const newAttachments: PendingAttachment[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: createPreviewUrl(file),
      status: "pending" as const,
      progress: 0,
    }));
    setPendingAttachments((prev) => [...prev, ...newAttachments]);
  }, []);

  const removeFile = useCallback((id: string) => {
    setPendingAttachments((prev) => {
      const attachment = prev.find((a) => a.id === id);
      if (attachment?.previewUrl) {
        revokePreviewUrl(attachment.previewUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  }, []);

  const updateAttachment = useCallback(
    (id: string, updates: Partial<PendingAttachment>) => {
      setPendingAttachments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, ...updates } : a)),
      );
    },
    [],
  );

  const uploadAttachments = useCallback(
    async (token: string): Promise<UploadedAttachment[]> => {
      const results: UploadedAttachment[] = [];

      for (const attachment of pendingAttachments) {
        if (attachment.status === "uploaded" && attachment.uploaded) {
          results.push(attachment.uploaded);
          continue;
        }

        updateAttachment(attachment.id, { status: "uploading", progress: 0 });

        try {
          const uploaded = await uploadFile(
            attachment.file,
            token,
            (progress) => {
              updateAttachment(attachment.id, { progress });
            },
          );
          updateAttachment(attachment.id, {
            status: "uploaded",
            progress: 100,
            uploaded,
          });
          results.push(uploaded);
        } catch (err) {
          const errorMsg = err instanceof Error ? err.message : "Upload failed";
          updateAttachment(attachment.id, { status: "error", error: errorMsg });
          toast.error(`Upload failed: ${errorMsg}`);
          // Stop uploading remaining files on first failure
          break;
        }
      }

      return results;
    },
    [pendingAttachments, updateAttachment],
  );

  const clearAttachments = useCallback(() => {
    pendingAttachments.forEach((a) => revokePreviewUrl(a.previewUrl));
    setPendingAttachments([]);
  }, [pendingAttachments]);

  const hasErrors = pendingAttachments.some((a) => a.status === "error");
  const isUploading = pendingAttachments.some((a) => a.status === "uploading");

  return {
    pendingAttachments,
    addFiles,
    removeFile,
    uploadAttachments,
    clearAttachments,
    hasErrors,
    isUploading,
  };
}
